import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { formatPrice } from "@/lib/utils";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, Power, PowerOff, ExternalLink, ArrowLeft } from "lucide-react";

const SPORTS = ["football", "tennis", "basketball", "padel", "volleyball", "badminton", "squash", "boxing", "swimming", "other"];

export default function AdminCourts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [courts, setCourts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [savingCourtId, setSavingCourtId] = useState(null);
  const [loading, setLoading] = useState(true);
  const LIMIT = 20;

  const fetchCourts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT, sort: "created_at", order: "desc" };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sportFilter) params.sport = sportFilter;
      const r = await api.get("/admin/courts", { params });
      setCourts(r.data.data);
      setTotal(r.data.total);
      setPages(r.data.pages);
    } catch {
      toast.error(t("common.registration_failed"));
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sportFilter, t]);

  useEffect(() => { fetchCourts(); }, [fetchCourts]);

  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const approveCourt = (courtId) => {
    setSavingCourtId(courtId);
    api.patch(`/admin/courts/${courtId}/approve`)
      .then((r) => {
        setCourts((prev) => prev.map((c) => (c.id === courtId ? r.data : c)));
        toast.success(t("admin.court_approved_toast"));
      })
      .catch(() => toast.error(t("common.registration_failed")))
      .finally(() => setSavingCourtId(null));
  };

  const rejectCourt = (courtId) => {
    setSavingCourtId(courtId);
    api.patch(`/admin/courts/${courtId}/reject`)
      .then((r) => {
        setCourts((prev) => prev.map((c) => (c.id === courtId ? r.data : c)));
        toast.success(t("admin.court_rejected_toast"));
      })
      .catch(() => toast.error(t("common.registration_failed")))
      .finally(() => setSavingCourtId(null));
  };

  const toggleActive = (court) => {
    setSavingCourtId(court.id);
    api.patch(`/admin/courts/${court.id}`, { active: !court.active })
      .then((r) => {
        setCourts((prev) => prev.map((c) => (c.id === court.id ? r.data : c)));
        toast.success(r.data.active ? "Court activated" : "Court paused");
      })
      .catch(() => toast.error(t("common.registration_failed")))
      .finally(() => setSavingCourtId(null));
  };

  const courtStatusStyles = (status) => {
    if (status === "approved") return "text-volt";
    if (status === "rejected") return "text-red-400";
    return "text-yellow-400";
  };

  const headers = [t("admin.th.name"), "Sport", "City", t("admin.th.price"), t("admin.th.status"), t("admin.th.actions")];

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground hover:text-volt transition-colors mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        <div className="label-eyebrow text-volt mb-2">{t("admin.eyebrow")}</div>
        <h1 className="font-heading text-5xl md:text-6xl tracking-tight mb-10">{t("admin.tabs.courts")}</h1>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("admin.search_placeholder")}
              className="w-full bg-card border border-border ps-10 pe-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-volt outline-none transition-colors"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-card border border-border rounded-sm px-3 py-2.5 text-sm text-foreground/80 focus:border-volt outline-none uppercase tracking-wider appearance-none cursor-pointer"
          >
            <option value="">{t("admin.filter_all_status")}</option>
            <option value="pending">{t("admin.court_pending")}</option>
            <option value="approved">{t("admin.court_approved")}</option>
            <option value="rejected">{t("admin.court_rejected")}</option>
          </select>
          <select
            value={sportFilter}
            onChange={(e) => { setSportFilter(e.target.value); setPage(1); }}
            className="bg-card border border-border rounded-sm px-3 py-2.5 text-sm text-foreground/80 focus:border-volt outline-none uppercase tracking-wider appearance-none cursor-pointer"
          >
            <option value="">{t("admin.filter_all_sports")}</option>
            {SPORTS.map((s) => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground/60">{t("admin.total_items", { total })}</span>
        </div>

        <div className="border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="text-start p-3 text-[10px] tracking-widest uppercase text-muted-foreground/80 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={headers.length} className="p-6 text-center text-muted-foreground/60 text-xs">{t("common.loading")}</td></tr>
              ) : courts.length === 0 ? (
                <tr><td colSpan={headers.length} className="p-6 text-center text-muted-foreground/60 text-xs">{t("admin.no_results")}</td></tr>
              ) : courts.map((c) => (
                <tr key={c.id} className="border-t border-border/30">
                  <td className="p-3">
                    <button onClick={() => navigate(`/courts/${c.id}`)} className="font-semibold hover:text-volt transition-colors text-start">
                      {c.name}
                    </button>
                  </td>
                  <td className="p-3 text-muted-foreground/80 uppercase text-xs tracking-wider">{c.sport}</td>
                  <td className="p-3">{c.city}</td>
                  <td className="p-3 text-volt font-bold">{formatPrice(c.price_per_slot, c.currency)}</td>
                  <td className="p-3">
                    <span className={`text-[10px] tracking-widest uppercase font-bold ${courtStatusStyles(c.status)}`}>
                      {c.status === "approved" ? t("admin.court_approved") : c.status === "rejected" ? t("admin.court_rejected") : t("admin.court_pending")}
                    </span>
                  </td>
                  <td className="p-3">
                    {c.status === "pending" ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => approveCourt(c.id)}
                          disabled={savingCourtId === c.id}
                          className="text-[9px] tracking-widest uppercase font-bold border border-emerald-400 text-emerald-400 px-2.5 py-1.5 hover:bg-emerald-400/10 transition-colors disabled:opacity-30"
                        >
                          {t("admin.court_approve")}
                        </button>
                        <button
                          onClick={() => rejectCourt(c.id)}
                          disabled={savingCourtId === c.id}
                          className="text-[9px] tracking-widest uppercase font-bold border border-red-400 text-red-400 px-2.5 py-1.5 hover:bg-red-400/10 transition-colors disabled:opacity-30"
                        >
                          {t("admin.court_reject")}
                        </button>
                        {savingCourtId === c.id && <span className="text-[10px] text-muted-foreground/60 animate-pulse">Saving...</span>}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleActive(c)}
                          disabled={savingCourtId === c.id}
                          className={`text-[9px] tracking-widest uppercase font-bold border px-2.5 py-1.5 transition-colors disabled:opacity-30 ${
                            c.active
                              ? "border-volt/50 text-volt hover:bg-volt/10"
                              : "border-muted-foreground/30 text-muted-foreground/60 hover:bg-muted"
                          }`}
                          title={c.active ? "Pause court" : "Activate court"}
                        >
                          {c.active ? <Power className="h-3 w-3 inline mr-1" /> : <PowerOff className="h-3 w-3 inline mr-1" />}
                          {c.active ? "Active" : "Paused"}
                        </button>
                        <Link
                          to={`/owner/courts/${c.id}/edit`}
                          className="text-[9px] tracking-widest uppercase font-bold border border-muted-foreground/30 text-muted-foreground/60 px-2.5 py-1.5 hover:bg-muted transition-colors inline-flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" /> Edit
                        </Link>
                        {savingCourtId === c.id && <span className="text-[10px] text-muted-foreground/60 animate-pulse">...</span>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex items-center gap-1 text-xs tracking-widest uppercase text-muted-foreground/80 hover:text-foreground transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-3 w-3" /> {t("admin.prev_page")}
            </button>
            <span className="text-xs text-muted-foreground/80">{t("admin.page_of", { page, pages })}</span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="flex items-center gap-1 text-xs tracking-widest uppercase text-muted-foreground/80 hover:text-foreground transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            >
              {t("admin.next_page")} <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
